/**
 * Bank-account picker shared across the deposit-propose modal,
 * the commitment-contract launch dialog, and the client profile
 * Bank Accounts section.
 *
 * Behaviour:
 *  - Lists the client's active client_bank_accounts as radio rows.
 *  - "Add new bank account" expands an inline form (type/country/account
 *    number/holder/nickname) and persists via bankAccountsService.create
 *    on save.
 *  - Selection is informational in Sprint 1 — the parent dialog reads
 *    `selectedAccountId` and stores it on its own state. Future sprints
 *    can wire it to deposit_request.client_bank_account_id once that FK
 *    is added.
 *  - No urgency / dark patterns in the empty state — the "register
 *    your bank account" copy is soft.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import bankAccountsService, {
  type BankAccountType,
  type ClientBankAccount,
  type CreateBankAccountPayload,
} from '../../services/bankAccounts.service';
import { getApiErrorCode, getApiErrorMessage } from '../../lib/apiError';
import { describeMoneyRefusal, type MoneyRefusal } from '../../lib/moneyRefusal';
import RefusalNotice from './RefusalNotice';
import { colors } from '../../styles/theme';
import { readerCountryName } from '../../lib/countries';
import { accountNumberForServer } from '../../lib/bankAccountNumber';
import StepUpCodeField from '../security/StepUpCodeField';


const TYPE_TO_COUNTRY: Record<BankAccountType, { country: string; currency: string }> = {
  CLABE: { country: 'MX', currency: 'MXN' },
  ABA:   { country: 'US', currency: 'USD' },
  CPA:   { country: 'CA', currency: 'CAD' },
};

const ACCOUNT_NUMBER_PLACEHOLDERS: Record<BankAccountType, string> = {
  CLABE: '18 digits',
  ABA:   '4 to 17 digits',
  CPA:   '5-3-7 digits',
};

interface Props {
  /** Client whose accounts to manage. Self-client passes own id; agents pass the target. */
  clientId: string;
  /** Optional: pre-select an account id (used when re-opening a draft). */
  initialSelectedId?: string | null;
  /** Fires whenever the selection changes (including after add-new save). */
  onSelectionChange?: (accountId: string | null) => void;
  /** When true, hides the radio rows and shows only the add-new form
   *  (used by ProfileBankAccountsSection where deactivate lives in
   *  the parent surface). Default false. */
  hideSelection?: boolean;
  /** Open the add form at once instead of behind the "+ Add bank account" button. */
  startOpen?: boolean;
  /** Account type fixed by the series' currency (MXN is a CLABE, USD an ABA account, CAD a CPA
   *  one): the type picker is not shown. */
  fixedType?: BankAccountType;
  /** Text of the save button; "Save & use" by default. */
  saveLabel?: string;
}

export default function BankAccountSelector({
  clientId,
  initialSelectedId = null,
  onSelectionChange,
  hideSelection = false,
  startOpen = false,
  fixedType,
  saveLabel,
}: Readonly<Props>): JSX.Element {
  const [accounts, setAccounts] = useState<ClientBankAccount[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(initialSelectedId);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(startOpen);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await bankAccountsService.listForClient(clientId);
      setAccounts(rows);
    } catch (e: unknown) {
      setError(getApiErrorMessage(e, 'Could not load bank accounts.'));
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { reload(); }, [reload]);

  const handleSelect = useCallback((id: string) => {
    setSelectedId(id);
    onSelectionChange?.(id);
  }, [onSelectionChange]);

  const handleCreated = useCallback((created: ClientBankAccount) => {
    setAccounts((prev) => [...prev, created]);
    setShowAddForm(false);
    handleSelect(created.id);
  }, [handleSelect]);

  if (loading) {
    return <p className="text-sm" style={{ color: colors.muted }}>Loading bank accounts…</p>;
  }

  return (
    <div>
      {error && (
        <div className="rounded-lg p-3 mb-3 text-sm" role="alert"
             style={{ background: colors.dangerBg, color: colors.danger,
                      border: `1px solid ${colors.dangerBorder}` }}>
          {error}
        </div>
      )}

      {!hideSelection && accounts.length > 0 && (
        <ul className="space-y-2 mb-3">
          {accounts.map((a) => (
            <li key={a.id}>
              <label className="flex items-center gap-3 p-3 rounded-lg cursor-pointer"
                     aria-label={`Select bank account ${a.nickname || a.holder_name}`}
                     style={{ background: selectedId === a.id ? colors.canvas : 'transparent',
                              border: `1px solid ${selectedId === a.id ? colors.gold : colors.border}` }}>
                <input
                  type="radio"
                  name="bank-account"
                  value={a.id}
                  checked={selectedId === a.id}
                  onChange={() => handleSelect(a.id)}
                />
                <div className="flex-1 min-w-0">
                  <div className="font-medium" style={{ color: colors.ink }}>
                    {a.nickname || a.holder_name}
                  </div>
                  <div className="text-xs font-mono" style={{ color: colors.muted }}>
                    {a.account_type} · {readerCountryName(a.country_code)} · ····{a.account_number_last4}
                  </div>
                </div>
              </label>
            </li>
          ))}
        </ul>
      )}

      {!hideSelection && !error && accounts.length === 0 && !showAddForm && (
        <p className="text-sm mb-3" style={{ color: colors.muted }}>
          No bank accounts registered yet. Adding one helps us match your
          transfers faster.
        </p>
      )}

      {!showAddForm && (
        <button
          type="button"
          onClick={() => setShowAddForm(true)}
          className="text-sm px-3 py-1.5 rounded-lg"
          style={{ background: colors.canvas, color: colors.inkSoft,
                   border: `1px solid ${colors.border}` }}
        >
          + Add bank account
        </button>
      )}

      {showAddForm && (
        <AddBankAccountForm
          clientId={clientId}
          askCode={accounts.length > 0}
          fixedType={fixedType}
          saveLabel={saveLabel}
          onSaved={handleCreated}
          onCancel={startOpen && accounts.length === 0 ? undefined : () => setShowAddForm(false)}
        />
      )}
    </div>
  );
}


// ── Add-new form ─────────────────────────────────────────────────


interface AddFormProps {
  clientId: string;
  /** The client already has an account: another one is confirmed with a code. The FIRST asks
   *  none (the server decides; if it asks anyway, the field appears). */
  askCode: boolean;
  fixedType?: BankAccountType;
  saveLabel?: string;
  onSaved: (account: ClientBankAccount) => void;
  /** Absent when the form is the only way forward (nothing to cancel back to). */
  onCancel?: () => void;
}

function AddBankAccountForm({
  clientId,
  askCode,
  fixedType,
  saveLabel,
  onSaved,
  onCancel,
}: Readonly<AddFormProps>): JSX.Element {
  const [accountType, setAccountType] = useState<BankAccountType>(fixedType ?? 'CLABE');
  const [codeAsked, setCodeAsked] = useState(false);
  const [accountNumber, setAccountNumber] = useState('');
  const [routingNumber, setRoutingNumber] = useState('');
  const [holderName, setHolderName] = useState('');
  const [nickname, setNickname] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refusal, setRefusal] = useState<MoneyRefusal | null>(null);

  const { country, currency } = useMemo(() => TYPE_TO_COUNTRY[accountType], [accountType]);

  const accountNumberPlaceholder = ACCOUNT_NUMBER_PLACEHOLDERS[accountType];
  const needsCode = askCode || codeAsked;

  const submit = async (): Promise<void> => {
    setSaving(true);
    setError(null);
    setRefusal(null);
    try {
      const payload: CreateBankAccountPayload = {
        account_type: accountType,
        country_code: country,
        currency_code: currency,
        account_number: accountNumberForServer(accountType, accountNumber, routingNumber),
        holder_name: holderName.trim(),
        nickname: nickname.trim() || null,
        ...(needsCode ? { mfa_code: mfaCode.trim() } : {}),
      };
      const created = await bankAccountsService.create(clientId, payload);
      onSaved(created);
    } catch (e: unknown) {
      // A hold on the account, or a country we cannot bank, is an explanation the
      // client needs here — not a red box saying the form failed to save.
      const designed = describeMoneyRefusal(e);
      if (designed !== null) {
        setRefusal(designed);
      } else if (['mfa_not_enrolled', 'step_up_code_required'].includes(getApiErrorCode(e) ?? '')) {
        // The server asks for the code (a second account, or an account added before): the
        // field, with the set-up of the authenticator when there is none, appears here.
        setCodeAsked(true);
      } else {
        setError(getApiErrorMessage(e, 'Could not save bank account.'));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-lg p-4 mt-3"
         style={{ background: colors.canvas, border: `1px solid ${colors.border}` }}>
      <h3 className="text-sm font-semibold mb-3" style={{ color: colors.ink }}>
        Add bank account
      </h3>

      {refusal && <RefusalNotice refusal={refusal} />}

      {error && (
        <div className="rounded-lg p-2 mb-3 text-xs" role="alert"
             style={{ background: colors.dangerBg, color: colors.danger,
                      border: `1px solid ${colors.dangerBorder}` }}>
          {error}
        </div>
      )}

      <div className="space-y-3">
        {!fixedType && (
        <div>
          <label htmlFor="bankacct-type" className="block text-xs mb-1" style={{ color: colors.muted }}>Type</label>
          <select
            id="bankacct-type"
            value={accountType}
            onChange={(e) => setAccountType(e.target.value as BankAccountType)}
            className="w-full px-3 py-2 rounded-lg text-sm"
            style={{ background: colors.surface, color: colors.ink,
                     border: `1px solid ${colors.border}` }}
          >
            <option value="CLABE">CLABE — Mexico</option>
            <option value="ABA">ABA — United States</option>
            <option value="CPA">CPA / EFT — Canada</option>
          </select>
          <p className="text-xs mt-1" style={{ color: colors.muted }}>
            Country: {readerCountryName(country)} · Currency: {currency}
          </p>
        </div>
        )}

        {accountType === 'ABA' && (
          <div>
            <label htmlFor="bankacct-routing" className="block text-xs mb-1" style={{ color: colors.muted }}>Routing number</label>
            <input
              id="bankacct-routing"
              type="text"
              inputMode="numeric"
              value={routingNumber}
              onChange={(e) => setRoutingNumber(e.target.value.replace(/\D/g, '').slice(0, 9))}
              className="w-full px-3 py-2 rounded-lg text-sm font-mono"
              placeholder="9 digits"
              style={{ background: colors.surface, color: colors.ink,
                       border: `1px solid ${colors.border}` }}
            />
          </div>
        )}

        <div>
          <label htmlFor="bankacct-number" className="block text-xs mb-1" style={{ color: colors.muted }}>Account number</label>
          <input
            id="bankacct-number"
            type="text"
            value={accountNumber}
            onChange={(e) => setAccountNumber(e.target.value)}
            className="w-full px-3 py-2 rounded-lg text-sm font-mono"
            placeholder={accountNumberPlaceholder}
            style={{ background: colors.surface, color: colors.ink,
                     border: `1px solid ${colors.border}` }}
          />
        </div>

        <div>
          <label htmlFor="bankacct-holder" className="block text-xs mb-1" style={{ color: colors.muted }}>Holder name</label>
          <input
            id="bankacct-holder"
            type="text"
            value={holderName}
            onChange={(e) => setHolderName(e.target.value)}
            className="w-full px-3 py-2 rounded-lg text-sm"
            style={{ background: colors.surface, color: colors.ink,
                     border: `1px solid ${colors.border}` }}
          />
        </div>

        <div>
          <label htmlFor="bankacct-nickname" className="block text-xs mb-1" style={{ color: colors.muted }}>
            Nickname (optional)
          </label>
          <input
            id="bankacct-nickname"
            type="text"
            value={nickname}
            onChange={(e) => setNickname(e.target.value)}
            placeholder="e.g. BBVA principal"
            className="w-full px-3 py-2 rounded-lg text-sm"
            style={{ background: colors.surface, color: colors.ink,
                     border: `1px solid ${colors.border}` }}
          />
        </div>

        {needsCode && (
          <StepUpCodeField
            id="bankacct-mfa"
            value={mfaCode}
            onChange={setMfaCode}
            hint="From your authenticator app. Adding another account needs a code."
          />
        )}
      </div>

      <div className="flex justify-end gap-2 mt-4">
        {onCancel && (
          <button type="button" onClick={onCancel}
                  className="text-sm px-3 py-1.5 rounded-lg"
                  style={{ background: 'transparent', color: colors.inkSoft,
                           border: `1px solid ${colors.border}` }}>
            Cancel
          </button>
        )}
        <button type="button" onClick={() => { submit(); }}
                disabled={saving || !accountNumber || (accountType === 'ABA' && routingNumber.length !== 9)
                          || !holderName || (needsCode && mfaCode.trim().length < 6)}
                className="text-sm px-3 py-1.5 rounded-lg font-semibold"
                style={{ background: colors.gold, color: colors.navy }}>
          {saving ? 'Saving…' : (saveLabel ?? 'Save & use')}
        </button>
      </div>
    </div>
  );
}
